import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { InteractiveItemComponent } from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { extractXmlProps, upsertChildren, xmlElementToString } from '@lfw/roesberg.liveforms.core';
import { EditorDialogService } from '../../../shared/services/editor-dialog.service';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { MatIcon } from '@angular/material/icon';
import { hasValidGroupPermission } from '../../../../utils/hasValidGroupPermission';
import { SignatureCanvasDialogComponent } from '../../../shared/dialogs/signature-canvas-dialog/signature-canvas-dialog.component';
import { INTERACTIVE_DIALOG_SIZE_STYLES } from '../../../../utils/constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { extractColorProperties, getColors, getTextColorsPropertiesStyle } from '../../../../utils/colors';

interface SignatureItemProperties {
  text: string;
  groupItemIds: string[];
  groupItems: string[];
  signed: boolean;
  label: string;
  style: string;
}

const base64Header = 'data:image/png;base64,';

@Component({
  selector: 'lfe-signature-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './signature-item-form.component.html',
  standalone: true,
  styleUrl: './signature-item-form.component.css'
})
export class SignatureItemFormComponent
  extends InteractiveItemComponent<SignatureItemProperties>
  implements OnInit, OnDestroy
{
  private readonly editorDialogService = inject(EditorDialogService);
  private readonly dialog = inject(MatDialog);

  readonly translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;
  private valueChangeSubscription?: Subscription;

  properties: InteractiveItemProperties & SignatureItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    groupItemIds: [],
    groupItems: [],
    text: this.translateService.instant('liveforms-editor.signature-item.default-text'),
    signed: false,
    label: this.translateService.instant('liveforms-editor.signature-item.label'),
    style: 'Normal'
  };

  formControlValue = signal('');

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `signature_${this.currentId()}`);

  currentTextColor = signal('');
  textStyle = computed(() => getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor()));

  initialValue = '';

  dataUrl = computed(() => {
    const value = this.formControlValue();
    return value ? `${base64Header}${value}` : '';
  });

  hasValue = computed(() => !!this.formControlValue());

  signatureInfo = computed(() => {
    const user = this.properties.resultUser || this.user().displayName;
    const dateOptions: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const date = this.properties.resultTime
      ? new Date(this.properties.resultTime).toLocaleString('de', dateOptions)
      : new Date().toLocaleString('de', dateOptions);
    return this.translateService.instant('liveforms-editor.signature-item.signature-info', { user, date });
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlId = this.properties.customId || this.properties.id;
    this.initialValue = this.properties.resultValue || '';
    this.formControl = new FormControl(this.initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    this.formControlValue.set(this.initialValue);

    this.valueChangeSubscription = this.formControl.valueChanges.subscribe((value) => {
      this.formControlValue.set(value || '');
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.valueChangeSubscription?.unsubscribe();
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & SignatureItemProperties {
    const props = extractXmlProps(xml);

    const sGroupItemIds = props['GroupItemIDs'];
    const sGroupItems = props['GroupItems'];

    const groupItemIds: string[] = sGroupItemIds ? sGroupItemIds.split(';').map((id) => id.trim()) : [];
    const groupItems: string[] = sGroupItems
      ? sGroupItems.split(';').map((name) => name.trim())
      : [this.translateService.instant('liveforms-editor.common.default-group-name')];

    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));
    this.currentTextColor.set(textColor);

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id: props['ID'],
      groupItemIds,
      groupItems,
      customId: props['CustomID'],
      activationRule: props['ActivationRule'],
      resultValue: props['ResultValue'],
      resultUser: props['ResultUser'],
      resultTime: props['ResultTime'],
      text: props['Text'] || this.translateService.instant('liveforms-editor.signature-item.default-text'),
      signed: props['Signed'] === 'True',
      label: props['Label'] || this.translateService.instant('liveforms-editor.signature-item.label'),
      style: props['Style'] || 'Normal',
      textBgColor,
      isGenericRepeatItem
    };
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    if (this.formControlValue() !== this.initialValue) {
      const signed = this.formControl?.value ? 'True' : 'False';

      upsertChildren(element, {
        Signed: signed
      });

      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }

  onSignatureOpenClick(e: Event): void {
    e.preventDefault();

    const dialogRef = this.dialog.open(SignatureCanvasDialogComponent, {
      ...INTERACTIVE_DIALOG_SIZE_STYLES,
      disableClose: false,
      data: {
        title: this.translateService.instant('liveforms-editor.signature-item.default-text')
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((base64: string | undefined) => {
        if (base64) {
          this.properties.resultValue = base64;
          this.properties.resultUser = this.user().displayName || '';
          this.properties.resultTime = new Date().toISOString();

          this.formControl?.setValue(base64);
          this.formControl?.markAsDirty();
          this.formControl?.markAsTouched();
        }
      });
  }

  canSign = computed(() =>
    hasValidGroupPermission({
      properties: this.properties,
      defaultGroupName: this.translateService.instant('liveforms-editor.common.default-group-name'),
      userGroups: this.user().userGroups
    })
  );

  async resetSignature(): Promise<void> {
    const result = await this.editorDialogService.showUserPrompt({
      title: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.title'),
      message: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.message'),
      icon: 'info',
      iconColor: '#1976d2',
      actions: [
        {
          text: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.actions.yes'),
          action: 'confirm',
          color: 'primary',
          variant: 'raised'
        },
        {
          text: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.actions.no'),
          action: 'cancel',
          color: 'primary',
          variant: 'basic'
        },
        {
          text: this.translateService.instant('liveforms-editor.signature-item.dialog.reset.actions.cancel'),
          action: 'abort',
          color: 'primary',
          variant: 'basic'
        }
      ]
    });

    if (result === 'confirm') {
      this.formControl?.setValue('');
      this.formControl?.markAsDirty();
      this.formControl?.markAsTouched();
    }
  }
}
